@if (isVisible) {
    <div class="fixed inset-0 w-screen h-screen overflow-y-auto bg-gray-500 bg-opacity-70 text-slate-900 p-2 z-[100]">
        <div class="flex items-center justify-center pt-20">
            <div #importModalContainer class="w-[350px] sm:w-[550px] min-h-[200px] bg-slate-100 rounded card-shadow flex flex-col text-base">
                <!-- Title -->
                <div class="flex flex-row bg-ponder-light text-slate-50 stroke-slate-50 rounded-t shadow pr-2">
                    <div class="p-2 grow font-bold">Import Deck</div>
                    <button (click)="onClose()" class="w-8 h-8 p-2 stroke-faded-50 hover:stroke-white hover:cursor-pointer">
                        <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5">
                            <title>Close</title>
                            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="closeSvgIcon" />
                        </svg>
                    </button>
                </div>
                <!-- Form -->
                <form [formGroup]="importDeckForm" class="flex flex-col gap-2 bg-slate-50 p-2">
                    <!-- Name -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Name:
                        </div>
                        <input formControlName="name" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                    </div>
                    <!-- Group -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Group:
                        </div>
                        <input formControlName="group" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm" value="Default" list="deckGroups">
                        @if (isVisible) {
                            <datalist id="deckGroups">
                                @for (group of deckGroups ; track group) {
                                    <option [attr.value]="group"></option>
                                }
                            </datalist>
                        }
                    </div>
                    <!-- Game -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Game:
                        </div>
                        <select #gameSelect formControlName="game" (change)="onGameChange(gameSelect.value)" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                            <option>Magic</option>
                        </select>
                    </div>
                    <!-- Type -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Type:
                        </div>
                        <select formControlName="type" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                            @for (deckType of deckTypes ; track deckType) {
                                <option value="{{ deckType }}">{{ deckType }}</option>
                            }
                        </select>
                    </div>
                    <!-- Visibility -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Visibility:
                        </div>
                        <select formControlName="visibility" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                            @for (visibilityType of visibilityTypes ; track visibilityType[0]) {
                                <option value="{{ visibilityType[0] }}">{{ visibilityType[1] }}</option>
                            }
                        </select>
                    </div>
                    <!-- Color -->
                    <div class="flex flex-row gap-1 items-center">
                        <div class="w-[12ch] text-right">
                            Banner Color:
                        </div>
                        <div class="flex align-middle items-center p-2 pl-4">
                            <label class="w-5 h-5 rounded-sm shadow-sm" [style.background-color]="currColor">
                                <!-- Note: We have to register our own (change) callback here as we want to update prevColor when selector is closed. The form change callback calls on all changes (ie when sliding picker around). -->
                                <input #colorInput formControlName="color" class="invisible" type="color" size="0" width="0" height="0" [attr.value]="currColor" (change)="onColorChange(colorInput.value)">
                            </label> 
                        </div>
                        <div class="grow">{{ currColor }}</div>
                        @if (prevColor) {
                            <div class="pr-7">
                                <button (click)="onUndoColorClicked()" class="w-5 h-5 p-1">
                                    <svg class="w-4 h-4 fill-none stroke-ponder hover:stroke-ponder-link" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="2">
                                        <title>Undo</title>
                                        <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="undoSvgIcon" />
                                    </svg>
                                </button>
                            </div>
                        }
                    </div>
                    <!-- Format -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Format:
                        </div>
                        <select formControlName="format" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                            @for (format of allFormats ; track format) {
                                @if (!$first) {
                                    <option value="{{ format }}">{{ getFileFormatDisplayName(format) }}</option>
                                }
                            }
                        </select>
                    </div>
                    <!-- Contents -->
                    <div class="flex flex-row gap-2 items-start">
                        <div class="w-[12ch] text-right pt-2">
                            Contents:
                        </div>
                        <div class="pl-2 pr-2 pt-2 grow">
                            <textarea formControlName="content" class="bg-white rounded shadow-sm w-full p-2 text-xs font-mono overflow-auto text-nowrap text-slate-600" rows="6">

                            </textarea>
                        </div>
                    </div>
                    <!-- Card Action -->
                    <div class="flex flex-row gap-2 items-center">
                        <div class="w-[12ch] text-right">
                            Card Action:
                        </div>
                        <select formControlName="cardAction" class="bg-white ml-2 mr-2 p-1 grow rounded-sm shadow-sm">
                            <option value="none">Add None to Collection</option>
                            <option value="all">Add All to Collection</option>
                            <option value="new">Add New to Collection</option>
                        </select>
                    </div>
                    <!-- Precon -->
                    @if (isPreconCheckboxVisible) {
                        <div class="flex flex-col gap-1 mt-2 mb-2 ml-8 mr-8 border p-1 rounded bg-slate-100 shadow-sm">
                            <div class="flex flex-row gap-2 items-center">
                                <div>
                                    Is Precon:
                                </div>
                                <input #isPreconCheckbox (change)="isPrecon = isPreconCheckbox.checked" type="checkbox">
                            </div>
                            <div class="flex flex-row gap-2 items-center">
                                <div>
                                    Date Override:
                                </div>
                                <input #dateOverrideInput (change)="dateOverrideInput.value" type="date">
                            </div>
                            <div class="text-xs italic text-slate-400">
                                Note this will create the deck under the "official" game account, make it visible in the appropriate set, and register it as a search term.
                            </div>
                        </div>
                    }
                </form>
                <!-- Action -->
            <div class="p-2 flex flex-row items-center gap-2 align-middle border-t text-sm justify-end">
                    @if (importFailed) {
                        <div class="text-sm text-red-600 grow">
                            {{ errorMessage ?? "Something went wrong! Please close and try again." }}
                        </div>
                    }
                    @if (!isImporting) {
                        <button (click)="onClose()" class="p-2 w-20 bg-slate-50 hover:bg-white rounded-sm shadow">
                            Cancel
                        </button>
                        <button (click)="onImportClicked()" class="p-2 w-20 bg-slate-50 hover:bg-white disabled:hover:bg-slate-50 disabled:text-slate-300 rounded-sm shadow" [disabled]="!canImport()">
                            Import
                        </button>
                    } @else {
                        <div class="flex flex-row justify-center items-center p-2 w-20 bg-slate-50">
                            <busy-spin [small]="true"></busy-spin>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
}